// GiraffeData backed by live SDK state.
//
// Reads are cached against the identity of the raw state object, so a broadcast
// that did not actually change a source costs nothing, and a recalculation that
// touches a hundred GI.* cells normalises the feature list once.

import type { DataRow, GiraffeData, MeasureClass, SourceKind } from '@/data/types';
import { classifyAll } from '@/data/classify';
import { areaDataRows } from '@/data/areasTable';
import turfArea from '@turf/area';
import { drawingLayerNames, normalizeFeatures, normalizeUsages, visibleDrawingLayerNames } from './normalize';
import { readBlocks } from './adapter';

/** `'areas'` is never a broadcast key — it is computed from the other three. */
type BroadcastSource = Exclude<SourceKind, 'areas'>;

interface SourceCache {
  raw: unknown;
  rows: DataRow[];
  fields: string[];
  classes: Map<string, MeasureClass>;
}

export class LiveGiraffeData implements GiraffeData {
  readonly live = true;

  version = 0;

  private raw: Record<BroadcastSource, unknown> = {
    bakedSections: null,
    rawSections: null,
    usages: null,
  };
  private cache = new Map<SourceKind, SourceCache>();
  /** `'areas'`'s own cache — invalidated against all THREE broadcast sources it derives from, not one. */
  private areasCache: { bakedRaw: unknown; rawRaw: unknown; usagesRaw: unknown; entry: SourceCache } | null = null;
  private selection: Set<string> | null = null;
  /** Section id -> geometry, kept for flyTo. */
  private geometryById = new Map<string, unknown>();
  /** The project boundary's area in m², measured once per project change. */
  private site: number | null = null;
  /** Drawing layers, off `layerTree`. Recomputed only when the tree changes. */
  private layers: readonly string[] = [];
  /** Subset of `layers` not switched off in the layer panel — see `visibleDrawingLayers`. */
  private visibleLayers: readonly string[] = [];

  /** Returns true when the value is actually new, so callers can skip a recalc. */
  /** Straight off the broadcast state; the shape juggling is in the adapter. */
  blocks(): Readonly<Record<string, { id: string; name?: string; properties?: Record<string, unknown> }>> {
    return readBlocks();
  }

  /**
   * Last-seen JSON per source. Giraffe re-broadcasts sections as fresh objects
   * with the same contents (on load, on the retry ladder, on unrelated state
   * changes), and identity alone read each one as a change: a full rewrite of
   * every system sheet and a recalc of every formula, several times per load.
   * Equal content keeps the old object, so nothing downstream rebuilds.
   */
  private rawJson = new Map<BroadcastSource, string>();

  setSource(source: BroadcastSource, value: unknown): boolean {
    if (this.raw[source] === value) return false;
    let json: string | undefined;
    try {
      json = JSON.stringify(value);
    } catch {
      json = undefined;
    }
    if (json !== undefined && json === this.rawJson.get(source)) return false;
    if (json === undefined) this.rawJson.delete(source);
    else this.rawJson.set(source, json);
    this.raw[source] = value;
    this.cache.delete(source);
    this.version += 1;
    return true;
  }

  /**
   * Multi-select lives on the untyped `lensSelection`, not `selected`. But a
   * project with no active lens broadcasts `lensSelection: []` forever, so
   * prefer it only while it is non-empty and fall back to `selected`.
   */
  setSelection(lensSelection: unknown, selected: unknown): boolean {
    const lensIds = idsOf(lensSelection);
    const ids = lensIds.length > 0 ? lensIds : idsOf(selected);
    const next = ids.length > 0 ? new Set(ids) : null;
    if (sameSet(next, this.selection)) return false;
    this.selection = next;
    this.version += 1;
    return true;
  }

  private read(source: SourceKind): SourceCache {
    if (source === 'areas') return this.readAreas();
    const hit = this.cache.get(source);
    if (hit && hit.raw === this.raw[source]) return hit;
    const rows =
      source === 'usages' ? normalizeUsages(this.raw[source]) : normalizeFeatures(this.raw[source]);
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.properties)) set.add(k);
    const fields = [...set].sort();
    const entry: SourceCache = { raw: this.raw[source], rows, fields, classes: classifyAll(fields, rows) };
    this.cache.set(source, entry);
    if (source === 'bakedSections') this.indexGeometry(this.raw[source]);
    return entry;
  }

  /** `'areas'`: computed from `mergeAreaRows` (areasTable.ts) over the three broadcast sources, not a broadcast key itself. */
  private readAreas(): SourceCache {
    const hit = this.areasCache;
    if (
      hit &&
      hit.bakedRaw === this.raw.bakedSections &&
      hit.rawRaw === this.raw.rawSections &&
      hit.usagesRaw === this.raw.usages
    ) {
      return hit.entry;
    }
    const rows = areaDataRows(this);
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.properties)) set.add(k);
    const fields = [...set].sort();
    const entry: SourceCache = { raw: null, rows, fields, classes: classifyAll(fields, rows) };
    this.areasCache = {
      bakedRaw: this.raw.bakedSections,
      rawRaw: this.raw.rawSections,
      usagesRaw: this.raw.usages,
      entry,
    };
    return entry;
  }

  rows(source: SourceKind): readonly DataRow[] {
    const all = this.read(source).rows;
    // A block marked "selection only" filters through selectionKeys(); the
    // unfiltered list is what every other caller wants.
    return all;
  }

  fields(source: SourceKind): readonly string[] {
    return this.read(source).fields;
  }

  classOf(source: SourceKind, field: string): MeasureClass {
    return this.read(source).classes.get(field) ?? 'rate';
  }

  /**
   * Measure the project boundary. Called when the `project` state key changes,
   * not per formula — turf on a boundary polygon is cheap but not free, and a
   * sheet can hold hundreds of GI.SITEAREA calls.
   */
  setProject(project: unknown): boolean {
    const next = measureBoundary(project);
    if (next === this.site) return false;
    this.site = next;
    this.version += 1;
    return true;
  }

  siteArea(): number | null {
    return this.site;
  }

  /**
   * Read on the `layerTree` broadcast rather than per call.
   *
   * Giraffe rebroadcasts the tree on every hover of a layer's eye icon, and the
   * walk is over the whole tree — so the list is compared by CONTENT, and a
   * broadcast that did not change which layers exist costs one comparison and
   * does not bump the version.
   */
  setLayerTree(layerTree: unknown): boolean {
    const next = drawingLayerNames(layerTree);
    const nextVisible = visibleDrawingLayerNames(layerTree);
    const sameNames = next.length === this.layers.length && next.every((l, i) => l === this.layers[i]);
    const sameVisible =
      nextVisible.length === this.visibleLayers.length && nextVisible.every((l, i) => l === this.visibleLayers[i]);
    if (sameNames && sameVisible) return false;
    this.layers = next;
    this.visibleLayers = nextVisible;
    this.version += 1;
    return true;
  }

  drawingLayers(): readonly string[] {
    return this.layers;
  }

  visibleDrawingLayers(): readonly string[] {
    return this.visibleLayers;
  }

  inferredClassOf(source: SourceKind, field: string): MeasureClass {
    return this.classOf(source, field);
  }

  selectionKeys(): ReadonlySet<string> | null {
    if (!this.selection) return null;
    // The map selects by feature id; our rows are keyed id + geometry signature.
    const out = new Set<string>();
    for (const row of this.read('bakedSections').rows) {
      if (this.selection.has(row.id)) out.add(row.key);
    }
    return out;
  }

  /** Geometry for a section id, so a row click can fly the map to it. */
  geometryFor(id: string): unknown {
    return this.geometryById.get(id);
  }

  private indexGeometry(raw: unknown): void {
    this.geometryById.clear();
    const walk = (node: unknown, depth: number): void => {
      if (node == null || depth > 4) return;
      if (Array.isArray(node)) {
        for (const x of node) walk(x, depth + 1);
        return;
      }
      if (typeof node !== 'object') return;
      const obj = node as Record<string, unknown>;
      if (Array.isArray(obj.features)) {
        walk(obj.features, depth + 1);
        return;
      }
      if ('geometry' in obj) {
        const props = (obj.properties ?? {}) as Record<string, unknown>;
        const id = obj.id ?? props.id;
        if (id != null && !this.geometryById.has(String(id))) {
          this.geometryById.set(String(id), obj.geometry);
        }
        return;
      }
      for (const v of Object.values(obj)) walk(v, depth + 1);
    };
    walk(raw, 0);
  }
}

/**
 * The project's own polygon, in m².
 *
 * `project` arrives in more than one shape across builds — a Feature, a bare
 * geometry, a FeatureCollection of one, or the geometry tucked under
 * `properties`. Trying only the documented one returned #N/A on a real project,
 * which then made plot ratio and site cover unavailable with nothing to explain
 * why. So try every shape, and only give up when none of them holds a polygon.
 */
function measureBoundary(project: unknown): number | null {
  for (const geometry of candidateGeometries(project)) {
    try {
      const value = turfArea({ type: 'Feature', geometry, properties: {} } as never);
      if (Number.isFinite(value) && value > 0) return value;
    } catch {
      // Not a usable geometry — try the next shape.
    }
  }
  return null;
}

function candidateGeometries(project: unknown, depth = 0): unknown[] {
  if (!project || typeof project !== 'object' || depth > 3) return [];
  const obj = project as Record<string, unknown>;

  // A bare geometry.
  if (typeof obj.type === 'string' && Array.isArray(obj.coordinates)) return [obj];

  const out: unknown[] = [];
  if (obj.geometry) out.push(obj.geometry);

  const props = obj.properties as Record<string, unknown> | undefined;
  if (props?.geometry) out.push(props.geometry);

  // A collection: the project polygon is whichever member is a polygon.
  if (Array.isArray(obj.features)) {
    for (const f of obj.features) out.push(...candidateGeometries(f, depth + 1));
  }
  // Some builds nest it one deeper.
  if (obj.project) out.push(...candidateGeometries(obj.project, depth + 1));

  return out.filter(
    (g) =>
      g !== null &&
      typeof g === 'object' &&
      /Polygon/i.test(String((g as Record<string, unknown>).type ?? ''))
  );
}

function idsOf(input: unknown): string[] {
  if (input == null) return [];
  const out: string[] = [];
  const walk = (node: unknown, depth: number): void => {
    if (node == null || depth > 3) return;
    if (typeof node === 'string' || typeof node === 'number') {
      out.push(String(node));
      return;
    }
    if (Array.isArray(node)) {
      for (const x of node) walk(x, depth + 1);
      return;
    }
    if (typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    if (Array.isArray(obj.features)) {
      walk(obj.features, depth + 1);
      return;
    }
    const props = (obj.properties ?? {}) as Record<string, unknown>;
    const id = obj.id ?? props.id;
    if (id != null) out.push(String(id));
  };
  walk(input, 0);
  return out;
}

function sameSet(a: Set<string> | null, b: Set<string> | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
